/**
 * Navigation Debug Instrument
 *
 * Module-level ring buffer + on-screen panel for tab/URL navigation races.
 * Kept in-repo for the next debug sprint; **disconnected by default**.
 *
 * Opt-in (reconnects recording + panel):
 *   - Append `?navdebug=1` to the URL (persists in sessionStorage for the tab)
 *   - Press Cmd/Ctrl+Shift+Option+N
 *   - Run `__navDebug.enable()` / `__navDebug.disable()` in the console
 *
 * When disabled: `record` / `recordUrlSync` / tab-timing helpers are no-ops.
 * Call sites stay wired so re-enabling does not require a code change.
 */
import type { NavigationEntry } from "@/hooks/use-navigation-history"

export type NavAction =
  | "navigate"
  | "goBack"
  | "goForward"
  | "snapshot"
  | "urlSync"

export type UrlSyncDirection = "tab-to-url" | "url-to-tab"

export interface NavStateSnapshot {
  historyLen: number
  index: number
}

export interface NavEvent {
  ts: number
  seq: number
  action: NavAction
  slotId: string
  caller?: string
  entry?: Omit<NavigationEntry, "timestamp"> | null
  result?: NavigationEntry | null
  stateBefore?: NavStateSnapshot
  stateAfter?: NavStateSnapshot
  /** Only populated for `snapshot` events. */
  fullState?: Record<string, NavStateSnapshot>
  /** URL-sync decisions (defer / skip / navigate / onUrlChange). */
  urlSync?: {
    direction: UrlSyncDirection
    outcome: "navigate" | "onUrlChange" | "skip"
    reason: string
    pathname?: string
    activeTabId?: string | null
    targetUrl?: string
  }
}

const MAX_EVENTS = 500
const MAX_TAB_TIMINGS = 50
/** Bumped so leftover sprint sessions do not auto-open the panel. */
const SESSION_KEY = "flownotes:navdebug:v2"
const MOUNTED_KEY = "__navDebug"

/** Wall-clock tab open timing (performance.now); pairs with tab-handlers + document-content. */
export interface TabOpenTiming {
  documentId: string
  clickAt: number
  fetchStartAt?: number
  fetchEndAt?: number
  tabCreatedAt?: number
  contentVisibleAt?: number
}

function perfNow(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now()
}

let tabTimings: TabOpenTiming[] = []
/** In-flight timing for the most recent document open initiated from tab handlers. */
let activeTabTiming: TabOpenTiming | null = null

/** Local Cursor ingest (NDJSON tooling). Production builds must not call localhost. */
const CURSOR_INGEST_URL =
  "http://127.0.0.1:7939/ingest/b988e4d1-dcac-44e0-a5d4-0bb77c471955"

/**
 * Sends one navigation debug row to Cursor local NDJSON ingest when enabled.
 * Opt-in: set `VITE_NAV_DEBUG_INGEST=true` in `.env` so dev does not spam
 * `ERR_CONNECTION_REFUSED` when nothing listens on port 7939. Production builds never send.
 * Also requires nav debug mode on (see `navDebug.enable()`).
 */
export function postCursorNavigationIngest(body: Record<string, unknown>): void {
  if (typeof fetch === "undefined") return
  if (!panelEnabled) return
  const ingestOn =
    typeof import.meta.env !== "undefined" &&
    String(import.meta.env.VITE_NAV_DEBUG_INGEST ?? "") === "true"
  if (!ingestOn) return
  fetch(CURSOR_INGEST_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Debug-Session-Id": "76985a",
    },
    body: JSON.stringify(body),
  }).catch(() => {})
}

let events: NavEvent[] = []
let seqCounter = 0
/** Master switch: panel visible + recording + optional ingest. Off by default. */
let panelEnabled = false
let listeners: Array<() => void> = []

function notify() {
  for (const fn of listeners) {
    try {
      fn()
    } catch (err) {
      console.error("[navDebug] listener threw:", err)
    }
  }
}

function persistEnabled(value: boolean) {
  if (typeof window === "undefined") return
  try {
    if (value) {
      window.sessionStorage.setItem(SESSION_KEY, "1")
    } else {
      window.sessionStorage.removeItem(SESSION_KEY)
    }
  } catch {
    // sessionStorage may be unavailable (Safari private mode); ignore
  }
}

export const navDebug = {
  /** True when recording is on and the on-screen debug panel should render. */
  isPanelEnabled(): boolean {
    return panelEnabled
  },
  /** Alias for call sites that care about recording, not UI. */
  isEnabled(): boolean {
    return panelEnabled
  },
  enable(): void {
    if (panelEnabled) return
    panelEnabled = true
    persistEnabled(true)
    notify()
  },
  disable(): void {
    if (!panelEnabled) return
    panelEnabled = false
    persistEnabled(false)
    // Drop buffered events so a later enable starts clean.
    events = []
    seqCounter = 0
    tabTimings = []
    activeTabTiming = null
    notify()
  },
  toggle(): void {
    if (panelEnabled) this.disable()
    else this.enable()
  },
  /**
   * Append an event to the ring buffer. No-op when debug mode is off
   * (call sites stay wired; reconnect via enable / ?navdebug=1).
   */
  record(event: Omit<NavEvent, "ts" | "seq">): void {
    if (!panelEnabled) return
    const full: NavEvent = {
      ts: Date.now(),
      seq: ++seqCounter,
      ...event,
    }
    events.push(full)
    if (events.length > MAX_EVENTS) {
      events = events.slice(-MAX_EVENTS)
    }
    notify()

    // NDJSON ingest for Cursor debug mode (skip snapshot: high volume, low signal)
    if (full.action !== "snapshot") {
      const hypothesisId =
        full.action === "goBack"
          ? "H2_goBack"
          : full.action === "goForward"
            ? "H3_goForward"
            : full.action === "urlSync"
              ? "H4_urlSync"
              : "H1_navigate"
      postCursorNavigationIngest({
        sessionId: "76985a",
        hypothesisId,
        location: "nav-debug.ts:record",
        message: full.action,
        data: {
          slotId: full.slotId,
          caller: full.caller ?? null,
          docId: full.entry?.documentId ?? full.result?.documentId ?? null,
          folderPath: full.entry?.folderPath ?? full.result?.folderPath ?? null,
          idxBefore: full.stateBefore?.index ?? null,
          lenBefore: full.stateBefore?.historyLen ?? null,
          idxAfter: full.stateAfter?.index ?? null,
          lenAfter: full.stateAfter?.historyLen ?? null,
          urlSync: full.urlSync ?? null,
        },
        timestamp: full.ts,
      })
    }
  },

  /**
   * Record a URL-sync decision (tab-to-URL or URL-to-tab). No-op when disabled.
   * Use reason strings that are stable enough to filter (e.g. `defer-bar`, `same-url`, `rate-limit`).
   */
  recordUrlSync(opts: {
    direction: UrlSyncDirection
    outcome: "navigate" | "onUrlChange" | "skip"
    reason: string
    pathname?: string
    activeTabId?: string | null
    targetUrl?: string
  }): void {
    if (!panelEnabled) return
    this.record({
      action: "urlSync",
      slotId: "*",
      caller: opts.direction,
      urlSync: {
        direction: opts.direction,
        outcome: opts.outcome,
        reason: opts.reason,
        pathname: opts.pathname,
        activeTabId: opts.activeTabId,
        targetUrl: opts.targetUrl,
      },
    })
  },
  getEvents(): NavEvent[] {
    return events.slice()
  },
  clear(): void {
    events = []
    seqCounter = 0
    tabTimings = []
    activeTabTiming = null
    notify()
  },
  /** Subscribe to enable/disable + event-recorded changes. Returns unsubscribe. */
  subscribe(fn: () => void): () => void {
    listeners.push(fn)
    return () => {
      listeners = listeners.filter((l) => l !== fn)
    }
  },
  /** Pretty-print recent events to the console (for quick inspection). */
  dump(limit = 50): void {
    const recent = events.slice(-limit)
     
    console.table(
      recent.map((e) => ({
        seq: e.seq,
        action: e.action,
        slot: e.slotId,
        caller: e.caller ?? "",
        before: e.stateBefore ? `${e.stateBefore.index}/${e.stateBefore.historyLen}` : "",
        after: e.stateAfter ? `${e.stateAfter.index}/${e.stateAfter.historyLen}` : "",
        docId: e.entry?.documentId ?? e.result?.documentId ?? "",
        urlSync: e.urlSync
          ? `${e.urlSync.outcome}:${e.urlSync.reason}`
          : "",
      })),
    )
  },

  /** Recent completed tab-open timings (content became visible or tab row updated without content hook). */
  getTimings(): TabOpenTiming[] {
    return tabTimings.slice()
  },
  clearTimings(): void {
    tabTimings = []
    activeTabTiming = null
    notify()
  },
  /** Start timing for opening `documentId` (replaces any in-flight timing). */
  beginTabOpenTiming(documentId: string): void {
    if (!panelEnabled) return
    activeTabTiming = { documentId, clickAt: perfNow() }
  },
  cancelTabOpenTiming(): void {
    activeTabTiming = null
  },
  markTabFetchStart(): void {
    if (!panelEnabled || !activeTabTiming) return
    activeTabTiming.fetchStartAt = perfNow()
  },
  markTabFetchEnd(): void {
    if (!panelEnabled || !activeTabTiming) return
    activeTabTiming.fetchEndAt = perfNow()
  },
  /**
   * Call after setOpenTabs for a new/replaced document tab. Finalizes when content reports visible,
   * or call without a follow-up content signal for non-document tabs.
   */
  markTabCreated(documentId?: string): void {
    if (!panelEnabled || !activeTabTiming) return
    if (documentId) activeTabTiming.documentId = documentId
    activeTabTiming.tabCreatedAt = perfNow()
  },
  /** Document panel finished first non-loading paint for this id (active tab only). */
  markTabContentVisible(documentId: string): void {
    if (!panelEnabled) return
    if (!activeTabTiming || activeTabTiming.documentId !== documentId) return
    const done: TabOpenTiming = {
      ...activeTabTiming,
      contentVisibleAt: perfNow(),
    }
    tabTimings.push(done)
    if (tabTimings.length > MAX_TAB_TIMINGS) {
      tabTimings = tabTimings.slice(-MAX_TAB_TIMINGS)
    }
    activeTabTiming = null
    notify()
  },
  /** Log intervals for the last completed timing (or in-flight partial). */
  lastTiming(): void {
    const last = tabTimings[tabTimings.length - 1]
    const cur = activeTabTiming
    const row = cur ?? last
    if (!row) {
       
      console.info("[navDebug] no tab timing recorded yet")
      return
    }
    const { clickAt, fetchStartAt, fetchEndAt, tabCreatedAt, contentVisibleAt, documentId } = row
    const dt = (t?: number) => (t != null ? `+${(t - clickAt).toFixed(1)} ms` : "n/a")
    const inFlight = row === cur
     
    console.info(
      `[navDebug] tab timing ${documentId}${inFlight ? " (in flight)" : ""}`,
      `\n  fetchStart: ${dt(fetchStartAt)}\n  fetchEnd: ${dt(fetchEndAt)}\n  tabCreated: ${dt(tabCreatedAt)}\n  contentVisible: ${dt(contentVisibleAt)}`,
    )
  },
}

/**
 * Initialize debug mode from URL param + sessionStorage. Idempotent: safe to
 * call multiple times. Should be invoked once at module load (auto-runs below).
 * Off unless explicitly opted in - no always-on recording.
 */
function initializeFromEnvironment() {
  if (typeof window === "undefined") return
  const urlEnabled =
    new URLSearchParams(window.location.search).get("navdebug") === "1"
  let sessionEnabled = false
  try {
    sessionEnabled = window.sessionStorage.getItem(SESSION_KEY) === "1"
  } catch {
    // ignore
  }
  if (urlEnabled || sessionEnabled) {
    panelEnabled = true
    if (urlEnabled) persistEnabled(true)
  }
  // Expose for console use even when disabled
  ;(window as unknown as Record<string, unknown>)[MOUNTED_KEY] = navDebug

  // Global keyboard shortcut: Cmd/Ctrl + Shift + Alt + N
  // Picked to avoid collision with existing FlowNotes shortcuts.
  window.addEventListener("keydown", (e) => {
    if (
      (e.metaKey || e.ctrlKey) &&
      e.shiftKey &&
      e.altKey &&
      (e.key === "n" || e.key === "N")
    ) {
      e.preventDefault()
      navDebug.toggle()
    }
  })
}

initializeFromEnvironment()
